import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { ButtonComponent } from '@shared/components/button';
import { IconDirective } from '@ui/components/icon';
import { Role } from '@shared/enums/role.enum';
import { PasswordResetCooldownService } from '../../services/password-reset-cooldown.service';
import { PasswordResetRequestResourceService } from '../../services/password-reset-request-resource.service';
import { AuthModalLayoutComponent } from '../auth-modal-layout/auth-modal-layout.component';
import { LOGIN_MODAL_SOURCES } from '../login-modal/constants/login.constant';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { ForgotPasswordSubmitDirective } from './directives/forgot-password-submit.directive';
import { ForgotPasswordFormControls } from './enums/forgot-password-form-controls.enum';
import { ForgotPasswordFormService } from './services/forgot-password-form.service';
import { CcsInputComponent } from '@shared/components/ccs-input/ccs-input.component';
import { FORGOT_PASSWORD_ERROR_MESSAGES } from './constants';

@Component({
  selector: 'forgot-password-modal',
  templateUrl: './forgot-password-modal.component.html',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    CcsInputComponent,
    IconDirective,
    AuthModalLayoutComponent,
    ForgotPasswordSubmitDirective,
  ],
  providers: [ForgotPasswordFormService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordModalComponent {
  protected readonly formControls = ForgotPasswordFormControls;

  private readonly _dialogRef = inject<DialogRef>(DialogRef);
  private readonly _dialog = inject(Dialog);
  private readonly _data = inject(DIALOG_DATA) as {
    state_geoname_code: string;
    role?: Role;
    source?: (typeof LOGIN_MODAL_SOURCES)[keyof typeof LOGIN_MODAL_SOURCES];
  };

  private readonly _formService = inject(ForgotPasswordFormService);
  private readonly _cooldown = inject(PasswordResetCooldownService);
  private readonly _resource = inject(PasswordResetRequestResourceService);

  protected readonly form = this._formService.form;

  protected readonly ERROR_MESSAGES = FORGOT_PASSWORD_ERROR_MESSAGES;
  protected readonly isLoading = this._resource.isLoading;
  protected readonly remainingSeconds = this._cooldown.remainingSeconds;
  protected readonly isCooldownActive = this._cooldown.isActive;

  protected readonly submitLabel = computed(() => {
    if (this.isLoading()) return 'Sending';
    if (this.isCooldownActive())
      return `Resend in ${formatSeconds(this.remainingSeconds())}`;
    return 'Reset Password';
  });

  protected close(event: MouseEvent) {
    event.preventDefault();
    this._dialogRef.close();
  }

  protected openLogin(event: MouseEvent) {
    event.preventDefault();

    this._dialogRef.close();
    this._dialog.open(LoginModalComponent, {
      data: {
        role: this._data.role ?? Role.Subscriber,
        source: this._data.source ?? LOGIN_MODAL_SOURCES.access_view,
      },
    });
  }

  protected get isSubmitDisabled() {
    return this.form.invalid || this.isLoading() || this.isCooldownActive();
  }
}

function formatSeconds(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(safe / 60)).padStart(2, '0');
  const ss = String(safe % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}
